import React from 'react';

import {
	IGeneratedTermEntityAttributes, StudyPeriodEntity, TermEntity, VersionTermConfigEntity,
} from 'Models/Entities';

import { DataIndex } from 'Util/DataIndexUtils';

import {
	IGeneratedPlan, RequisiteWarning, StudyLoadConfig, TermIndex, YearHalf,
} from 'Queries/Hooks/StudyPlan/types';

import StudyPlanTerm from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlan/StudyPlanTerm';
import Toggle from 'Views/Components/Toggle/Toggle';
import { Button, Display } from 'Views/Components/Button/Button';
import { toLower } from 'lodash';

interface StudyPlanYearProps {
	generatedPlan: IGeneratedPlan,
	requisiteWarningsByCourseId: DataIndex<RequisiteWarning[]>
	allTerms: TermEntity[],
	allStudyPeriods: StudyPeriodEntity[],
	currentYear: number,
	yearIndex: number,
	generatedTerms: IGeneratedTermEntityAttributes[],
	getCourseUnitTotal: (courseIds: string[]) => number,
	resetPlanStudyLoad: () => void,
	togglePeriodStudyLoad: (studyPlanId: string, year: number, period: number, setToDefaultLoad: boolean) => void,
	showResetButton: boolean;
	getCourseComponents: (courseId: string) => string[];
	disableResetButton: boolean;
	getProgramTermConfig: (period: YearHalf, term: TermIndex) => VersionTermConfigEntity;
	adjustTermStudyLoad: (
		action: 'add' | 'subtract', yearIndex: number, period: number, term: number
	) => void,
	getCurrentPeriodStudyLoad: (year: number, periodIndex: number) => number[] | undefined
	getCurrentTermStudyLoad: (year: number, periodIndex: number, termIndex: number) => number | undefined
}

const StudyPlanYear = (props: StudyPlanYearProps) => {
	const {
		generatedPlan,
		generatedTerms,
		currentYear,
		yearIndex,
		getCourseUnitTotal,
		resetPlanStudyLoad,
		allTerms,
		allStudyPeriods,
		adjustTermStudyLoad,
		requisiteWarningsByCourseId,
		togglePeriodStudyLoad,
		showResetButton,
		getCourseComponents,
		disableResetButton,
		getProgramTermConfig,
		getCurrentTermStudyLoad,
		getCurrentPeriodStudyLoad,
	} = props;

	const termsGroupedByPeriod = allStudyPeriods
		.map(studyPeriod => {
			const terms = generatedTerms.filter(term => term.periodOrder === studyPeriod.order);
			terms.sort((termA, termB) => termA.termOrder - termB.termOrder);
			const termIds = terms.map(term => term.id);

			const periodCourses = generatedPlan.plannedCourses
				.filter(
					plannedCourse => termIds.includes(plannedCourse.studyTermId),
				);

			return {
				studyPeriod,
				terms, // terms for this study period
				courses: periodCourses,
			};
		})
		.filter(orderedTerms => orderedTerms.terms.length > 0);

	return (
		<div className="study-plan-year">
			<div className="study-plan-year-row">
				<h2 className="study-plan-year__title">
					{currentYear}
				</h2>
				{
					showResetButton
					&& (
						<Button
							buttonProps={{ title: 'Reset study load of entire plan' }}
							className="load-reset-button icon icon-right icon-history"
							display={Display.Outline}
							onClick={resetPlanStudyLoad}
							disabled={disableResetButton}
						>
							Reset
						</Button>
					)
				}
			</div>
			{
				termsGroupedByPeriod
					.map(({ studyPeriod, terms, courses }) => {
						const studyLoadsForPeriod = getCurrentPeriodStudyLoad(currentYear, studyPeriod.order);
						const periodHasLoadCapacity = !!studyLoadsForPeriod?.find(termLoad => termLoad !== 0);

						const courseUnitTotal = getCourseUnitTotal(
							courses.map(course => course.courseEntityId),
						);

						const toggleAriaLabel = `period availability toggle${
							studyPeriod.name ? ` for ${studyPeriod.name}` : ''}`;

						return (
							<div
								key={`${currentYear}-${studyPeriod.id}`}
								className="study-period"
							>
								<div className="study-period-header-bar">
									<Toggle
										ariaLabel={toggleAriaLabel}
										className="period-availability-toggle"
										checked={periodHasLoadCapacity}
										onChange={e => {
											const shouldResetLoad = e.target.checked;
											togglePeriodStudyLoad(
												generatedPlan!.studyPlan.id,
												currentYear,
												studyPeriod.order,
												shouldResetLoad,
											);
										}}
									/>
									<h5>{studyPeriod?.name}</h5>
									<p>{courseUnitTotal} {toLower(window.lexicon.unitsShort)}</p>
								</div>
								{
									terms.map(generatedTerm => {
										const {
											defaultLoad, maximum, minimum,
										} = getProgramTermConfig(generatedTerm.periodOrder, generatedTerm.termOrder);

										const coursesForTerm = courses
											.filter(plannedCourse => plannedCourse.studyTermId === generatedTerm.id)
											.map(plannedCourse => plannedCourse.courseEntity);

										const term = allTerms
											.find(currentTerm => (
												currentTerm.studyPeriod.order === generatedTerm.periodOrder
												&& currentTerm.order === generatedTerm.termOrder
											));

										const existingLoad = getCurrentTermStudyLoad(
											currentYear,
											studyPeriod.order,
											generatedTerm.termOrder,
										);

										const termStudyLoad = existingLoad ?? defaultLoad;

										return (
											<StudyPlanTerm
												termName={term?.name || ''}
												key={`${currentYear}-${studyPeriod.id}-${generatedTerm.id}`}
												studyPlanId={generatedPlan.studyPlan.id}
												requisiteWarningsByCourseId={requisiteWarningsByCourseId}
												generatedTerm={generatedTerm}
												courses={coursesForTerm}
												maxStudyLoad={maximum}
												minStudyLoad={minimum}
												termStudyLoad={termStudyLoad}
												incrementPeriodStudyLoad={
													// eslint-disable-next-line max-len
													(...params) => adjustTermStudyLoad('add', currentYear, ...params)
												}
												decrementPeriodStudyLoad={
													// eslint-disable-next-line max-len
													(...params) => adjustTermStudyLoad('subtract', currentYear, ...params)
												}
												getCourseComponents={getCourseComponents}
											/>
										);
									})
								}
							</div>
						);
					})
			}
		</div>
	);
};

export default StudyPlanYear;
